import type { Habit, HabitId } from "../../../shared/types";
import { getCategoryLabel, getCategoryBarColor } from "../../../shared/stores/category-store";
import { useMemo } from "react";

interface HabitDailyViewProps {
  readonly habits: Habit[];
  readonly today: string;
  readonly getStreak: (id: HabitId) => number;
  readonly getBestStreak: (id: HabitId) => number;
  readonly onToggle: (id: HabitId, date: string) => void;
  readonly onEdit: (habit: Habit) => void;
}

export function HabitDailyView({
  habits,
  today,
  getStreak,
  getBestStreak,
  onToggle,
  onEdit,
}: HabitDailyViewProps) {
  // Obtenir les jours de la semaine courante pour vérifier les habitudes hebdomadaires
  const weekDays = useMemo(() => {
    const days: string[] = [];
    const todayDate = new Date(today + "T00:00:00");
    const dayOfWeek = todayDate.getDay();
    const monday = new Date(todayDate);
    monday.setDate(todayDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      days.push(`${y}-${m}-${day}`);
    }
    return days;
  }, [today]);

  function getCompletionRate(habit: Habit): number {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today + "T00:00:00");
      d.setDate(d.getDate() - i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    });
    return last7Days.filter((d) => habit.completedDates.includes(d)).length;
  }

  return (
    <div className="flex flex-col gap-1">
      {habits.map((habit) => {
        const isDone = habit.completedDates.includes(today);
        const streak = getStreak(habit.id);
        const best = getBestStreak(habit.id);
        const completion = getCompletionRate(habit);

        // Pour les habitudes hebdomadaires : vérifier si déjà validée cette semaine
        const isWeekly = habit.frequency === "weekly";
        const weeklyDoneInWeek = isWeekly
          ? weekDays.some((d) => habit.completedDates.includes(d))
          : false;
        const isWeeklyDone = isWeekly && weeklyDoneInWeek;

        return (
          <div
            key={habit.id}
            className="relative border-r border-border/40 transition-all duration-300 hover:bg-surface-alt/30"
          >
            <div
              className={`absolute left-0 top-0 bottom-0 w-0.5 rounded-full ${getCategoryBarColor(habit.category)}`}
            />
            <div className="flex items-center gap-3 pl-3 pr-2 py-2">
              <button
                type="button"
                onClick={() => !isWeeklyDone && onToggle(habit.id, today)}
                disabled={isWeeklyDone && !isDone}
                aria-label={`Marquer « ${habit.title} » comme ${isDone ? "non fait" : "fait"}`}
                className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold transition-colors ${
                  isDone || isWeeklyDone
                    ? "bg-green-500 text-white"
                    : "bg-surface-alt text-ink-faint border border-border/60 hover:border-accent/60"
                } ${isWeeklyDone && !isDone ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {isDone || isWeeklyDone ? (
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span className="text-[10px]">Auj.</span>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${isDone || isWeeklyDone ? "line-through text-ink-faint" : "text-ink"}`}
                >
                  {habit.title}
                </p>
                <p className="text-xs text-ink-faint">
                  {getCategoryLabel(habit.category)}{" "}
                  <span className="text-ink-faint/30 mx-1">·</span>
                  {habit.frequency === "daily" ? "Quotidien" : "Hebdo"}
                </p>
              </div>
              <div className="flex flex-col items-end gap-0.5 mr-1">
                {streak > 0 ? (
                  <span className="text-xs font-semibold text-accent" title="Série actuelle">
                    {streak}j
                  </span>
                ) : (
                  <span className="text-xs text-ink-faint">—</span>
                )}
                {best > 1 && best > streak && (
                  <span className="text-[10px] text-ink-faint" title="Meilleure série">
                    best {best}j
                  </span>
                )}
                <span className="text-[10px] text-ink-faint" title="Complétion sur 7 jours">
                  {completion}/7
                </span>
              </div>
              <button
                type="button"
                data-testid={`edit-habit-${habit.id}`}
                onClick={() => onEdit(habit)}
                aria-label={`Modifier ${habit.title}`}
                className="shrink-0 min-w-8 min-h-8 flex items-center justify-center text-ink-faint hover:text-ink rounded-lg hover:bg-surface-alt/50 transition-colors"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
